/* start - flights_split_bounds_us */
import { useClientFetch } from "../hooks/useClientFetch";
import useReloadIfSearchStoreMissing, { isSearchStoreInitial } from "../hooks/useReloadIfSearchStoreMissing";
import { useSearchAPIParams } from "../hooks/useSearchAPIParams";
import React, { Profiler, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSearch } from "../store/search/selectors";
import { actions as LoaderActions } from "../store/loadingScreen/actions";
import { actions as SearchResultsBoundsActions } from "../store/searchResultsBounds/actions";
import { actions as SearchCriteriaActions } from "../store/searchCriteria/actions";
import { Route, useLocation } from "react-router-dom";
import useRouteName from "../hooks/useRouteName";
import StaticContext from "../app/StaticContext/StaticContext";
import useClientMetrics from "../hooks/useClientMetrics";
import { PageHeader } from "./Search";
import { AppShellDesktop } from "../components/elements/AppShell";
import Container from "../components/elements/Container";
import { Box, HiddenVisually, Stack, Text } from "@bookingcom/bui-react";
import { t, useI18n } from "@bookingcom/lingojs-react";
import useSearchbox from "../hooks/useSearchbox";
import useLocaleContext from "../hooks/useLocaleContext";
import { SearchboxController, SearchBoxHorizontalDefault } from "@bookingcom/flights-searchbox";
import SearchResultsOutboundDesktop from "../components/elements/SearchResults/SearchResults.outbound.desktop";
import { UISearchResults } from "@flights/types";
import useRemoveExternalParamsFromUrl from "../hooks/useRemoveExternalParamsFromUrl";
import { goal, trackWithDefaultStage } from "@flights/et-universal";
import { getSearchCriteria } from "../store/getSearchCriteria";
import SearchLoadingScreen from "../components/elements/LoadingScreen/components/SearchLoadingScreen";
import { getSearchFetchStatus } from "../store/searchResultsBounds/outboundSelectors";
import { FLIGHT_DETAILS_OUTBOUND_ROUTE_PATH } from "@flights/constants";
import { HomeDesktop, FlightDetailsDesktop, SearchInboundDesktop } from "../app/routes";
import { trackMetaChannelStagesV2 } from "../utils/experiments/track-meta-landings-v2";
import { useFlightSearchResultsGTMTag } from "../hooks/useGTMFlightTags";
import useTrackPageLoad from "../hooks/useTrackPageLoad";
import { getVisible } from "../store/loadingScreen/selectors";
import { getMarketingTrackingVariable } from "@flights/web-api-utils-universal";
import useBlackFridayExp from "../components/elements/BlackFridayBanner/useBlackFridayExp";

const SearchOutboundDesktop = () => {
  const search = useSelector(getSearch);
  const fetchStatus = useSelector(getSearchFetchStatus);
  const loadingScreenVisible = useSelector(getVisible);
  const dispatch = useDispatch();

  const location = useLocation<{ hideLoader: boolean; forceRefetch: boolean }>();
  const routeName = useRouteName();
  const staticContext = useContext(StaticContext);
  const i18n = useI18n();

  const [lastTrackedPageLoad, setLastTrackedPageLoad] = useState<
    "search_results_outbound" | "search_results_skeleton" | "search_results_modal" | ""
  >("");
  const { trackRenderTime } = useClientMetrics();
  const fireByeahSearchResultsTag = useFlightSearchResultsGTMTag();
  const trackSRContextEventV2 = useTrackPageLoad("search_results_outbound");
  const trackSRSkeletonContextEventV2 = useTrackPageLoad("search_results_skeleton");
  const trackSRModalContextEventV2 = useTrackPageLoad("search_results_modal");
  const isBlackFridayExp = useBlackFridayExp();

  const queryParams = new URLSearchParams(location.search);
  if (process.env.BUILD_TARGET === "server" && staticContext.set) {
    const { getInitialState } = require("../server/screens/search");
    staticContext.set({ getInitialState: getInitialState(queryParams, { selectedBound: "outbound" }) });
  }

  useRemoveExternalParamsFromUrl();

  useEffect(() => {
    trackMetaChannelStagesV2("desktop");

    goal("flights_search_results_page_view");
    goal("tbu_vertical_search_results_view");
  }, []);

  // start -- FETCH DATA
  const searchAPIParams = useSearchAPIParams({ outboundOnly: true });
  const url = `/api/flights/?${searchAPIParams}`;
  const isMetaOrigin = getMarketingTrackingVariable("ext-origin");

  useClientFetch(url, {
    // delay the load of Search Results in background of Flight Details
    // when user lands on SR with a meta offerToken to temporarily fix an issue on ETG
    delayFetchBy: !!isMetaOrigin && routeName === "flightdetails" ? 500 : 0,
    condition: () => {
      if (isSearchStoreInitial(search)) {
        return false;
      }

      return true;
    },
    fetch: () => {
      // only show loading screen when coming from index / searchbox
      if (!location?.state?.hideLoader && !routeName.includes("searchresults")) {
        history.replaceState({ hideLoader: false }, document.title);

        dispatch(LoaderActions.show());

        setTimeout(() => {
          dispatch(LoaderActions.hide());
        }, 6000);
      }
      dispatch(SearchResultsBoundsActions.fetchOutbound());
    },
    success: (data: UISearchResults) => {
      dispatch(SearchCriteriaActions.setSearchCriteria(getSearchCriteria(queryParams, search)));
      dispatch(
        SearchResultsBoundsActions.fetchSuccessOutbound({
          ...data,
          // We only need the first segment for outbound.
          flightOffers: data.flightOffers.map((f) => ({ ...f, segments: [f.segments[0]] }))
        })
      );
      fireByeahSearchResultsTag(); // fire GTM flight search results tag
      dispatch(LoaderActions.hide());
    },
    error: (data) => {
      goal("flights_search_results_loading_error");
      dispatch(LoaderActions.hide());
      dispatch(SearchResultsBoundsActions.fetchErrorOutbound(data));
    },
    forceRefetch: location?.state?.forceRefetch
  });
  // END -- fetch data

  useEffect(() => {
    if (["initial", "loading"].includes(fetchStatus)) {
      if (loadingScreenVisible && lastTrackedPageLoad !== "search_results_modal") {
        trackSRModalContextEventV2();
        setLastTrackedPageLoad("search_results_modal");
      } else if (lastTrackedPageLoad !== "search_results_skeleton") {
        trackSRSkeletonContextEventV2();
        setLastTrackedPageLoad("search_results_skeleton");
      }
    } else if (lastTrackedPageLoad !== "search_results_outbound") {
      trackSRContextEventV2();
      setLastTrackedPageLoad("search_results_outbound");
    }
  }, [fetchStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const searchBoxProps = useSearchbox();
  const { isRTL } = useLocaleContext();
  const searchBoxControllerProps = { ...searchBoxProps, i18n, defaultSegmentLength: search.searchSegments.length };

  const [preloadDetails, toggle] = useState(false);
  if (!preloadDetails && fetchStatus === "success") {
    toggle(true);
    SearchInboundDesktop.preload();
    HomeDesktop.preload();
  }

  if (useReloadIfSearchStoreMissing(search)) {
    return null;
  }

  return (
    <Profiler id="screen" onRender={trackRenderTime}>
      <PageHeader segments={search.searchSegments} />
      <AppShellDesktop>
        <Stack grow={true} gap={6}>
          <Box padding={0} backgroundColor={"neutral_alt"} mixin={{ paddingBlockStart: 6, paddingBlockEnd: 6 }}>
            <Container>
              <HiddenVisually>
                <Text tagName="h1">{i18n.trans(t("a11y_flights_sr_search_hidden_heading"))}</Text>
              </HiddenVisually>
              <Stack gap={2}>
                <SearchboxController
                  {...searchBoxControllerProps}
                  isRTL={isRTL}
                  isResponsiveFixes={!!trackWithDefaultStage("flights_web_cat_responsive_fixes_www", 1)}
                  enabledCountriesAndAnywhere={true}
                  isBlackFridayExp={isBlackFridayExp}
                >
                  {() => <SearchBoxHorizontalDefault />}
                </SearchboxController>
              </Stack>
            </Container>
          </Box>

          <Container>
            <SearchResultsOutboundDesktop />
          </Container>

          <Route key={FLIGHT_DETAILS_OUTBOUND_ROUTE_PATH} path={FLIGHT_DETAILS_OUTBOUND_ROUTE_PATH} exact={true}>
            <FlightDetailsDesktop />
          </Route>

          {routeName.includes("searchresults") && <SearchLoadingScreen />}
        </Stack>
      </AppShellDesktop>
    </Profiler>
  );
};

export default React.memo(SearchOutboundDesktop);
/* end - flights_split_bounds_us */
